import {Component} from '@angular/core';
import {AlertController, LoadingController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {NEVER, of, zip} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';

import {getTranslation} from 'shared/helpers/get-translation';

import { DialogsPage } from 'app/onboarding';
import {NavService} from 'shared/nav/nav.service';
import {Answer, Dialog, Question} from '../../model/model';
import {DialogsService} from '../../services/dialogs.service';
import { DefaultPage } from 'app/menu/components/default-page/default-page';

@Component({
  selector: 'dialogs-questions-page',
  templateUrl: 'dialogs-questions-page.html',
  styleUrls: ['dialogs-questions-page.scss'],
})
export class DialogsQuestionsPage extends DefaultPage{

  private dialog: Dialog;
  public answers: Array<Answer>;

  constructor(
    public navService: NavService,
    public translateService: TranslateService,
    public dialogsService: DialogsService,
    public loadingCtrl: LoadingController,
    public alertCtrl: AlertController,
  ) {
    super(navService);
    this.dialog = this.navService.get('dialog');
  }

  public get dialogTitle(): string {
    if (this.dialog) {
      return getTranslation(this.dialog.title, this.translateService);
    }

    return '';
  }

  public get questions(): Array<Question> {
    if (this.dialog) {
      return this.dialog.questions;
    }

    return [];
  }

  public get isValid(): boolean {
    return this.validateAnswers();
  }

  private ionViewWillEnter(): void {
    this.answers = this.dialog.questions.map((question) => {
      return {
        questionId: question._id,
        answer: '',
      };
    });
  }

  public answerChanged(questionId: string, value: string): void {
    const changedAnswer: Answer = this.answers.find((answer) => { return answer.questionId === questionId; });
    if (changedAnswer) {
      changedAnswer.answer = value;
    }
  }

  public getQuestionTitle(question: Question): string {
    return getTranslation(question.text, this.translateService);
  }

  public getMinLabel(question: Question): string {
    return question.minLabel !== undefined ? getTranslation(question.minLabel, this.translateService) : undefined;
  }

  public getMaxLabel(question: Question): string {
    return question.maxLabel !== undefined ? getTranslation(question.maxLabel, this.translateService) : undefined;
  }

  private validateAnswers(): boolean {
    if (!this.answers) {
      return false;
    }

    for (const answer of this.answers) {
      if (typeof answer.answer === 'undefined' || answer.answer === '') {
        return false;
      }
    }

    return true;
  }

  public get goal(): boolean {
    return this.dialog.goal && this.dialog.goalId === undefined;
  }

  public onNextClick(): void {
    if (this.validateAnswers()) {
      this.sendAnswers(false);
    }
  }

  public onGoalClick(): void {
    if (this.validateAnswers()) {
      this.sendAnswers(true);
    }
  }

  private async sendAnswers(asGoal: boolean): Promise<void> {

    const loader: HTMLIonLoadingElement = await this.loadingCtrl.create({});
    loader.present().then(() => {
      this.dialogsService.postDialogAnswers(this.answers)
        .pipe(
          catchError((error) => {
            loader.dismiss().then(() => this.showErrorAlert());
            return NEVER;
          }),
          catchError((error) => {
            return of(false);
          }),
          finalize(() => {
            loader.dismiss().then(() => {
              if ( asGoal ) {
                this.navService.push('goals/edit', {dialog: this.dialog});
              } else if ( this.dialog.goalId !== undefined ) {
                this.navService.push('goals/feedback', {dialog: this.dialog});
              } else {
                this.navService.root('dashboard');
              }
            });
          }))
        .subscribe();
    });

  }

  private showErrorAlert(): void {
    zip(
      this.translateService.get('COMMON.ERROR'),
      this.translateService.get('COMMON.OK'),
      this.translateService.get('DIALOGS.COULD_NOT_SEND_ANSWERS'),
    ).subscribe(async(translations) => {
      const alert: HTMLIonAlertElement = await this.alertCtrl.create({
        subHeader: translations[0],
        buttons: [translations[1]],
        // tslint:disable-next-line:no-magic-numbers
        message: translations[2],
      });
      alert.present();
    });
  }
}
